import React from 'react';
import { Capacitor } from '@capacitor/core';
import { Box, BoxProps, useColorModeValue } from '@chakra-ui/react';
import * as Sentry from '@sentry/react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';

import { UserLocale } from 'clipsal-cortex-types/src/api/api-user';

import { IS_NATIVE } from '../../common/constants';
import { useUpdateLocaleMutation } from '../../features/account/accountApi';
import { SUPPORTED_LOCALES } from '../../i18n';
import { setupPushNotifications } from '../../utils/native/push-notifications';
import { CustomSelect, SelectOption } from './CustomSelect';

/**
 * Maps IETF BCP 47 language tags to our API locale names.
 *
 * @param locale - An IETF BCP 47 language tag (e.g. 'en-US', 'fr-CA', 'es-US').
 *
 * @returns The locale name in our API for the language tag
 */
function mapLocaleToApiValue(locale: string): UserLocale {
  switch (locale) {
    case 'en-US':
      return 'EN_US';
    case 'fr-CA':
      return 'FR_CA';
    case 'es-US':
      return 'ES_US';
    default:
      return 'EN_US'; // Default to English (US)
  }
}

export function LanguageSelector({ ...props }: BoxProps) {
  const { i18n, t } = useTranslation();
  const navigate = useNavigate();
  const backgroundColor = useColorModeValue('#FFFFFF', '#27282A');
  const [updateLocale] = useUpdateLocaleMutation();

  async function handleChangeLanguage(language: SelectOption | null) {
    try {
      if (language?.value) {
        i18n.changeLanguage(language.value);
        const userLocale = mapLocaleToApiValue(language.value);
        await updateLocale({ locale: userLocale }).unwrap();

        // Re-register push notifications with the new locale so backend sends notifications in the correct language
        if (IS_NATIVE && Capacitor.isNativePlatform()) {
          await setupPushNotifications(navigate, userLocale).catch((error) => {
            console.error('Failed to re-register push notifications after language change:', error);
          });
        }
      }
    } catch (e) {
      console.error(e);
      Sentry.captureException(e, {
        tags: {
          category: 'common',
          component: 'LanguageSelector',
          method: 'handleChangeLanguage',
        },
        extra: { language: JSON.stringify(language) },
      });
    }
  }

  return (
    <Box data-testid="language-selector" {...props}>
      <CustomSelect
        value={{
          value: i18n.language,
          label: SUPPORTED_LOCALES[i18n.language],
        }}
        onChange={handleChangeLanguage}
        options={Object.entries(SUPPORTED_LOCALES).map(([code, name]) => ({ value: code, label: name }))}
        placeholder={`${t('Common.select language')}...`}
        backgroundColor={backgroundColor}
        isSearchable={false}
      />
    </Box>
  );
}
